/**
 * Shared bits for the native (iPad) interactive auth flows that hand a
 * short-lived authorization code back to the app via a custom URL scheme. The
 * app exchanges the code for a session token (see `exchangeMobileAuthCode`).
 *
 * Two entry points produce these callback URLs:
 *   - Google, via ASWebAuthenticationSession (code in the URL fragment).
 *   - Magic link, via a deep link from the email confirmation page (code in
 *     the query string — Safari does not reliably preserve fragments when
 *     launching an app from a custom-scheme link).
 * The parser below accepts either location.
 */
import { MOBILE_OAUTH_CALLBACK_SCHEME } from "@lib/auth/shared/mobileOauthConstants";
import { invitationReturnPath } from "@lib/teamInvitations";

export const MOBILE_OAUTH_SCHEME = MOBILE_OAUTH_CALLBACK_SCHEME;

export type MobileAuthCallback = {
  code?: string;
  error?: string;
  returnTo?: string;
};

export function parseMobileAuthCallback(rawUrl: string): MobileAuthCallback {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return {};
  }

  if (url.protocol.replace(/:$/, "") !== MOBILE_OAUTH_SCHEME) {
    return {};
  }

  const fragmentParams = new URLSearchParams(url.hash.replace(/^#/, ""));
  const code = fragmentParams.get("code") ?? url.searchParams.get("code");
  const error = fragmentParams.get("error") ?? url.searchParams.get("error");

  return {
    code: code ?? undefined,
    error: error ?? undefined,
    returnTo: invitationReturnPath(url.searchParams.get("returnTo")),
  };
}
